import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useBoolean } from 'react-use'

import { OrgMemberRole, OrgPermission, TeamMemberRole } from '@makyoapp/makyo-sdk/models/ACL'
import { MemberAdapter, PaginatedItems, TeamItemAdapter } from '@makyoapp/makyo-common'
import { Block16, Edit16 } from '@makyoapp/makyo-icon/icon'
import {
  AvatarCell,
  AvatarSize,
  Badge,
  DeleteModal,
  getTeamRoleIcon,
  Loading,
  MoreDropdown,
  projectStatusColor,
  Status,
  Table,
  Td,
  Tr,
  userRoleColor,
} from '@makyoapp/makyo-ui'

import { useOrgsContext } from '@/context/OrgsContext'
import useAuth from '@/hooks/useAuth'
import { useDateFmt } from '@/hooks/useDateFmt'
import { MemberField } from '@/shared/constants'

import { EditRoleModal } from './EditRoleModal'
import { StackedAvatarTd } from './TdItem'
import { Thead } from './Thead'
import { TrNotFound } from './TrNotFound'

function TeamMemberItem({
  field,
  member,
  renderLink,
  isLoading,
}: {
  isLoading: boolean
  field: MemberField
  member: MemberAdapter
  renderLink?: (node: React.ReactNode, member: MemberAdapter) => React.ReactNode
}) {
  const { t } = useTranslation()
  const dateFmtDefault = useDateFmt()
  let node = null
  let teamRole, dt, avt, avtNode, selfAdded, avatarUrl, avatarAddedBy

  switch (field) {
    case 'teamRole':
      teamRole = member.teamRole || TeamMemberRole.Viewer
      return (
        <Td className='role text-m-sm'>
          <Badge color={userRoleColor(teamRole)}>
            <div className='flex-c1'>
              <span>{getTeamRoleIcon(teamRole)}</span>
              <span>{t(teamRole)}</span>
            </div>
          </Badge>
        </Td>
      )
    case 'joined':
    case 'invited':
      dt = member[field]
      return <Td className='date'>{dt ? dateFmtDefault(dt) : ''}</Td>
    case 'status':
      return (
        <Td className='status'>
          <Status className='uppercase' dotColor={projectStatusColor(member.status)}>
            {member.status}
          </Status>
        </Td>
      )
    case 'teams':
      return <StackedAvatarTd items={member.teams} />
    case 'name':
      avatarUrl = member.avatar ? `${member.avatar}?t=${Date.now()}` : member.avatar
      avt = <AvatarCell avatar={avatarUrl} name={member.name} email={member.email} size={AvatarSize.S} />
      avtNode = renderLink ? renderLink(avt, member) : avt
      return <Td className='name'>{isLoading ? <Loading fullHeight={false} /> : avtNode}</Td>
    case 'addedBy':
      selfAdded = member.addedBy?.id === member.userId
      if (member.addedBy && !selfAdded) {
        avatarAddedBy = member.addedBy.avatar ? `${member.addedBy.avatar}?t=${Date.now()}` : member.addedBy.avatar
        node = <AvatarCell size={AvatarSize.S} {...member.addedBy} avatar={avatarAddedBy} />
      }
      return <Td>{node}</Td>
    case 'addedOn':
      if (member.joined) {
        node = dateFmtDefault(member.joined)
      }
      return <Td className='date'>{node}</Td>
    default:
      return <Td className={field}>{member[field]?.toString()}</Td>
  }
}

interface TeamMemberTableProps {
  fields: MemberField[]
  isLoading: boolean
  members: PaginatedItems<MemberAdapter>
  //onEditClick: (team: TeamItemAdapter) => void
  onRemove: (member: MemberAdapter, closeModal: () => void) => void
  onUpdateRole: (member: MemberAdapter, newRole: TeamMemberRole, closeModal: () => void) => void
  team: TeamItemAdapter
}
export function TeamMemberTable({ isLoading, fields, members, onRemove, onUpdateRole, team }: TeamMemberTableProps) {
  const { t } = useTranslation()
  const { userData } = useAuth()
  const { orgsPerms } = useOrgsContext()

  const [memberToDelete, setMemberToDelete] = useState<MemberAdapter | undefined>(undefined)

  const canEditTeamMemberRole = orgsPerms[team.org.id]?.[OrgPermission.TeamsChangeMemberRole]

  const canRemoveTeamMember = orgsPerms[team.org.id]?.[OrgPermission.TeamsDeleteMembers]

  const [isMemberLoading, setIsMemberLoading] = useState('')
  const [isDeleteLoading, setIsDeleteLoading] = useBoolean(false)
  const [editRole, setEditRole] = useState<MemberAdapter | undefined>(undefined)
  const fieldLen = fields.length + 1
  return (
    <>
      <Table className='border bdr'>
        <Thead
          fields={fields.map((f) => {
            if (f === 'name') {
              return 'Member'
            }
            if (f === 'teamRole') {
              return 'Role'
            }

            return f
          })}
          withAction={true}
        />
        <tbody>
          {isLoading ? (
            <Tr>
              <Td colSpan={fieldLen}>
                <Loading fullHeight={false} />
              </Td>
            </Tr>
          ) : members.totalCount < 1 ? (
            <TrNotFound colSpan={fieldLen} />
          ) : (
            members.items.map((member, row) => {
              const canRemove = canRemoveTeamMember && member.userId !== userData?.id

              const hideMenu = member.orgRole === OrgMemberRole.Owner || !(canRemove || canEditTeamMemberRole)

              return (
                <Tr key={`member-${member.id}`}>
                  {fields.map((f, col) => {
                    return (
                      <TeamMemberItem
                        key={`${row}-${col}`}
                        field={f}
                        member={member}
                        isLoading={isMemberLoading ? isMemberLoading === member.id : false}
                      />
                    )
                  })}
                  <Td className='act' align='right'>
                    {!hideMenu && (
                      <MoreDropdown placement='bottom-end' menuClassName='text-m-sm' portalMenu={true}>
                        {canEditTeamMemberRole && (
                          <MoreDropdown.MenuItem
                            onClick={() => {
                              setEditRole(member)
                            }}
                          >
                            <Edit16 />
                            <span>{t('Edit Role')}</span>
                          </MoreDropdown.MenuItem>
                        )}
                        {canRemove && (
                          <MoreDropdown.MenuItem className='red' onClick={() => setMemberToDelete(member)}>
                            <Block16 />
                            <span>{t('Remove')}</span>
                          </MoreDropdown.MenuItem>
                        )}
                      </MoreDropdown>
                    )}
                  </Td>
                </Tr>
              )
            })
          )}
        </tbody>
      </Table>
      {canEditTeamMemberRole && editRole !== undefined && (
        <EditRoleModal
          show={true}
          onHide={() => setEditRole(undefined)}
          member={editRole}
          isTeam={true}
          onSubmit={(newRole) => {
            const membersToEdit = { ...editRole }
            setEditRole(undefined)
            setIsMemberLoading(membersToEdit.id)
            onUpdateRole(membersToEdit, newRole as TeamMemberRole, () => {
              setIsMemberLoading('')
            })
          }}
        />
      )}
      {canRemoveTeamMember && memberToDelete !== undefined && (
        <DeleteModal
          show={true}
          onHide={() => setMemberToDelete(undefined)}
          onSubmit={() => {
            setIsDeleteLoading(true)
            onRemove(memberToDelete, () => {
              setMemberToDelete(undefined)
              setIsDeleteLoading(false)
            })
          }}
          isLoading={isDeleteLoading}
          title={t('Remove member')}
          continueLabel={t('Yes, remove member')}
        >
          <div className='modal-sm'>
            <div className='text-base mb-2'>{t('Are you sure you’d like to delete this team member?')}</div>
            <div className='text-light'>
              <p className='mb-1'>
                {t('They will no longer have access to any apps or databases belonging to this team.')}
              </p>
              <p>
                {t(
                  'Removing team members does not remove them from the organization. You can remove someone from the organization by visiting the Members page.'
                )}
              </p>
            </div>
          </div>
        </DeleteModal>
      )}
    </>
  )
}
